import { useListState } from '@mantine/hooks';
import { fromPairs, isArray, isString, map } from 'lodash-es';

import { useQueryNormalizer } from '@repo/react-query-cache';
import type { Data } from '@repo/react-query-cache/core';
import { useCallback, useEffect } from 'react';
import type { ICreateWidgetParams, IWidget, IWidgetSample } from '../../../api';
import {
	useCreateWidget,
	useDeleteWidget,
	useReport,
	useUpdateHomepageReport,
	useUpdateWidget,
} from '../../../api';
import { usePopulateReportWithTemplate } from '../../../api/hooks/widget/usePopulateReportWithTemplate';
import {
	reorderWidgetsHelper,
	useReorderWidgets,
} from '../../../api/hooks/widget/useReorderWidgets';
import type {
	UpdatableWidgetKey,
	UpdatableWidgetKeys,
	UpdatableWidgetValue,
	WidgetUpdateFn,
} from '../types';

type IReportManagementArgs =
	| {
			type: 'teams';
			teamId: string;
	  }
	| {
			type: 'homepage';
	  }
	| {
			type: 'analytics';
			id: string;
	  };

export function useReportManagement(
	args: IReportManagementArgs = { type: 'homepage' }
) {
	const queryNormalizer = useQueryNormalizer();

	const { data: report, isFetching } = useReport({
		type: args.type,
		teamId: args.type === 'teams' ? args.teamId : undefined,
		reportId: args.type === 'analytics' ? args.id : undefined,
	});
	const { mutate: updateReport } = useUpdateHomepageReport();

	const [widgets, widgetsHandler] = useListState<IWidget>([]);
	const { mutateAsync: updateWidget } = useUpdateWidget();
	const { mutateAsync: deleteWidget } = useDeleteWidget();
	const { mutateAsync: createWidget } = useCreateWidget();
	const { mutateAsync: reorderWidgets } = useReorderWidgets();
	const { mutateAsync: populateReportWithTemplate } =
		usePopulateReportWithTemplate();

	useEffect(() => {
		widgetsHandler.setState(report?.widgets ?? []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [report?.widgets]);

	const handleBackgroundImageChange = (image = '', fontColor = '#000000') => {
		if (!report?.id) {
			return;
		}

		updateReport({
			data: {
				id: report?.id,
				background_image: image,
				header_font_color: fontColor,
			},
		});
	};

	const addWidget = useCallback(
		async (widgetSample: IWidgetSample) => {
			if (!report?.id) {
				return;
			}

			const data: ICreateWidgetParams['data'] = {
				report: report?.id,
				...widgetSample,
			};

			const createdWidget = await createWidget({
				data,
			});

			queryNormalizer.setNormalizedData({
				id: report.id,
				widgets: [createdWidget, ...report.widgets], // widgets are added to the beginning of the array (order=0)
			} as unknown as Data);
		},
		[createWidget, queryNormalizer, report?.id, report?.widgets]
	);

	const handleUpdateWidget: WidgetUpdateFn = useCallback(
		(widgetId: string, key: UpdatableWidgetKeys) =>
			async (value: UpdatableWidgetValue | UpdatableWidgetValue[]) => {
				let properties = {};

				if (isArray(key) && isArray(value)) {
					properties = fromPairs<IWidget[UpdatableWidgetKey]>(
						key.map((k, i) => [k, value?.[i]])
					);
				}

				if (isString(key)) {
					properties = {
						[key]: value,
					};
				}

				// Optimistically update widgets state
				const arrayId = widgets.findIndex((widget) => widget.id === widgetId);
				if (arrayId >= 0) {
					widgetsHandler.setItem(arrayId, {
						...widgets[arrayId],
						...properties,
					});
				}

				await updateWidget({
					data: {
						id: widgetId,
						...properties,
					},
				});
			},
		[updateWidget, widgets, widgetsHandler]
	);

	const handleRemoveWidget = useCallback(
		(id: string) => async () => {
			if (!report?.id) {
				return;
			}

			await deleteWidget({
				id,
			});

			queryNormalizer.setNormalizedData({
				id: report.id,
				widgets: widgets.filter((widget) => widget.id !== id),
			} as unknown as Data);
		},
		[deleteWidget, queryNormalizer, report?.id, widgets]
	);

	const handleReorderWidgets = useCallback(
		async ({ from, to }: { from: number; to: number }) => {
			if (!report?.id) {
				return;
			}

			const widgetIds = map(widgets, 'id');

			// Optimistically update widgets state
			widgetsHandler.reorder({ from, to });

			try {
				await reorderWidgets({
					data: {
						widgetIds,
						from,
						to,
					},
					reportId: report.id,
				});

				// Updated to match handleRemoveWidget and handleAddWidget
				queryNormalizer.setNormalizedData({
					id: report.id,
					widgets: reorderWidgetsHelper<IWidget>(widgets, from, to),
				} as unknown as Data);
				// casting to Data is no good, but until we refactor all uses of report widgets it is a necessary evil

				localStorage.setItem(`reorder_${report.id}`, 'true');
			} catch {
				// Revert changes if request failed
				widgetsHandler.reorder({ from: to, to: from });
			}
		},
		[reorderWidgets, report?.id, widgets, widgetsHandler]
	);

	const handlePopulateReportWithTemplate = useCallback(
		async (template: string) => {
			if (!report?.id) {
				return;
			}

			await populateReportWithTemplate({
				reportId: report.id,
				template,
			});
		},
		[populateReportWithTemplate, report?.id]
	);

	return {
		isFetching,
		report,
		widgets,
		updateBackgroundImage: handleBackgroundImageChange,
		addWidget,
		reorderWidgets: handleReorderWidgets,
		updateWidget: handleUpdateWidget,
		removeWidget: handleRemoveWidget,
		populateReportWithTemplate: handlePopulateReportWithTemplate,
	};
}
